class NativeScriptureChart {
    static DEFAULT_COLORS = {
        ns_highlight: 'rgb(212, 175, 55)',
        ns_base: 'rgb(184, 158, 80)'
    };

    static CHART_OPTIONS = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { beginAtZero: true, ticks: { display: true } },
            y: {
                grid: { display: false },
                ticks: {
                    font: { family: 'Times New Roman', size: 14, weight: 'bold', style: 'italic' }
                }
            }
        },
        plugins: {
            legend: { display: false },
            datalabels: {
                color: 'white',
                font: { family: 'Times New Roman', weight: 'bold' },
                anchor: 'center',
                align: 'center'
            }
        }
    };

    static AWS_COUNT_ENDPOINT = 'https://5a242lz7s1.execute-api.us-east-2.amazonaws.com/dev/count';

    constructor(shopifyMetadata) {
        this.completedItems = new Set();
        this.urls = {};
        this.keyMap = {};
        this.colors = NativeScriptureChart.DEFAULT_COLORS;
        this.MIN_SIZE_PERCENT = 0.10;
        this.chart = null;

        // Ensure shopifyMetadata is always an array
        // If it's a string (JSON), parse it first
        if (typeof shopifyMetadata === 'string') {
            try {
                this.shopifyMetadata = JSON.parse(shopifyMetadata);
                if (!Array.isArray(this.shopifyMetadata)) {
                    this.shopifyMetadata = [];
                }
            } catch (e) {
                console.error('Failed to parse shopifyMetadata JSON:', e);
                this.shopifyMetadata = [];
            }
        } else if (Array.isArray(shopifyMetadata)) {
            this.shopifyMetadata = shopifyMetadata;
        } else {
            this.shopifyMetadata = [];
        }

        // Initialize chart with empty data, will update after AWS fetch
        this.data = {};
        this.loadData();
    }

    async loadData() {
        try {
            // Fetch vote counts from AWS
            const response = await fetch(NativeScriptureChart.AWS_COUNT_ENDPOINT);

            if (!response.ok) {
                throw new Error(`AWS API error: ${response.status} ${response.statusText}`);
            }
            // Use response.text() then JSON.parse() - this works more reliably than response.json()
            const responseText = await response.text();
            const awsData = JSON.parse(responseText);

            // Merge AWS vote counts with Shopify metadata
            const mergedVotes = this.mergeData(awsData, this.shopifyMetadata);

            // Format and update chart
            this.data = this.formatData(mergedVotes);
            this.setMinSize(this.MIN_SIZE_PERCENT);
            this.updateBarChart();
        } catch (error) {
            console.error('Failed to load leaderboard data from AWS:', error);

            // Fallback: use Shopify metadata with vote_count of 0 if available
            // Ensure it's an array before calling map
            if (!Array.isArray(this.shopifyMetadata)) {
                this.shopifyMetadata = [];
            }

            const fallbackVotes = this.shopifyMetadata.map(item => ({
                ...item,
                vote_count: 0
            })).sort((a, b) => {
                // Sort: vote_count descending first, then by is_completed
                if (a.vote_count !== b.vote_count) {
                    return b.vote_count - a.vote_count;
                }
                return b.is_completed - a.is_completed;
            });

            this.data = this.formatData(fallbackVotes);
            this.setMinSize(this.MIN_SIZE_PERCENT);
            this.updateBarChart();
        }
    }

    mergeData(awsData, shopifyMetadata) {
        const merged = [];
        const awsLeaderboard = awsData.leaderboard || [];

        // Ensure shopifyMetadata is an array
        if (!Array.isArray(shopifyMetadata)) {
            shopifyMetadata = [];
        }

        // Create a map of Shopify metadata by normalized name (lowercase)
        const shopifyMap = new Map();
        shopifyMetadata.forEach(item => {
            const normalizedName = item.name.toLowerCase();
            shopifyMap.set(normalizedName, item);
        });

        // Process AWS votes and merge with Shopify data
        // Only include languages that exist in Shopify metadata
        awsLeaderboard.forEach(awsItem => {
            const normalizedLanguage = awsItem.language.toLowerCase();
            const shopifyItem = shopifyMap.get(normalizedLanguage);

            // Only include if it exists in Shopify metadata
            if (shopifyItem) {
                merged.push({
                    name: shopifyItem.name, // Use Shopify name (proper case)
                    vote_count: awsItem.count,
                    is_completed: shopifyItem.is_completed || false,
                    url: shopifyItem.url || null
                });
            }
        });

        // Include Shopify languages that don't exist in AWS (with 0 votes)
        shopifyMetadata.forEach(shopifyItem => {
            const normalizedName = shopifyItem.name.toLowerCase();
            const existsInAWS = awsLeaderboard.some(
                awsItem => awsItem.language.toLowerCase() === normalizedName
            );

            if (!existsInAWS) {
                merged.push({
                    name: shopifyItem.name,
                    vote_count: 0,
                    is_completed: shopifyItem.is_completed,
                    url: shopifyItem.url
                });
            }
        });

        // Sort: vote_count descending first, then by is_completed
        merged.sort((a, b) => {
            // First sort by vote_count descending
            if (a.vote_count !== b.vote_count) {
                return b.vote_count - a.vote_count;
            }
            // Then sort by is_completed (true first)
            return b.is_completed - a.is_completed; // true (1) comes before false (0)
        });

        return merged;
    }

    getMax(data) {
        return Math.max(0, ...Object.entries(data)
            .filter(([key]) => !this.completedItems.has(key))
            .map(([, value]) => value));
    }

    formatData(votes) {
        if (!Array.isArray(votes) || !votes.length) {
            console.error('Invalid or empty votes data');
            return {};
        }

        const formattedData = {};
        for (const item of votes) {
            // Skip invalid items
            if (!item || typeof item.name !== 'string') {
                continue;
            }
            const normalizedName = item.name.replace(/\s+/g, '_');
            this.keyMap[normalizedName] = item.name;

            if (item.is_completed) {
                this.completedItems.add(normalizedName);
                if (item.url) this.urls[normalizedName] = item.url;
            } else {
                this.completedItems.delete(normalizedName);
            }
            formattedData[normalizedName] = item.vote_count;
        }

        this.maxValue = this.getMax(formattedData);
        return formattedData;
    }

    sortData() {
        const entries = Object.entries(this.data);
        const completed = [];
        const notCompleted = [];

        for (const entry of entries) {
            (this.completedItems.has(entry[0]) ? completed : notCompleted).push(entry);
        }


        // completed.sort((a, b) => b[1] - a[1]);
        // notCompleted.sort((a, b) => b[1] - a[1]);
        // this.data = Object.fromEntries([...completed, ...notCompleted]);

        //sort all
        this.data = Object.fromEntries(Object.entries(this.data).sort((a, b) => b[1] - a[1]));
    }

    setMinSize(percent_max) {
        this.MIN_SIZE = percent_max * this.maxValue;
    }

    getBackgroundColors(entries) {
        return entries.map(([key]) =>
            this.completedItems.has(key) ? this.colors.ns_highlight : this.colors.ns_base
        );
    }

    /**
     * Checks if text fits inside the bar and returns appropriate positioning
     * @param {Object} context - Chart.js context object
     * @param {string} text - The text to display
     * @returns {Object} Object with anchor, align, and color properties
     */
    getLabelPosition(context, text) {
        const element = context.element;
        const chart = context.chart;
        const dataIndex = context.dataIndex;
        const dataValue = context.dataset.data[dataIndex];

        // Calculate bar width from data value and chart scale
        // For horizontal bar charts, we need to get the pixel width from the x-axis scale
        let barWidth = 0;

        // Try to get element from chart metadata (might be available there)
        let barElement = element;
        if (!barElement && chart && chart.getDatasetMeta) {
            const meta = chart.getDatasetMeta(context.datasetIndex);
            if (meta && meta.data && meta.data[dataIndex]) {
                barElement = meta.data[dataIndex];
            }
        }

        if (barElement && barElement.width) {
            // If element is available, use it directly
            barWidth = barElement.width;
        } else if (chart && chart.scales && chart.scales.x && typeof chart.scales.x.getPixelForValue === 'function') {
            try {
                // Calculate from the scale: bar width = pixel position of value - pixel position of 0
                const xScale = chart.scales.x;
                const valuePixel = xScale.getPixelForValue(dataValue);
                const zeroPixel = xScale.getPixelForValue(0);
                barWidth = Math.abs(valuePixel - zeroPixel);
            } catch (e) {
                barWidth = 0;
            }
        }

        // If bar width is not available yet, default to center
        if (!barWidth || barWidth <= 0) {
            return {
                anchor: 'center',
                align: 'center',
                color: 'white'
            };
        }

        // Get font settings - try to get from context first, then fall back to defaults
        const datalabelsConfig = context.dataset?.datalabels || NativeScriptureChart.CHART_OPTIONS.plugins.datalabels;
        const fontConfig = datalabelsConfig?.font || {};
        const fontFamily = fontConfig.family || 'Times New Roman';
        const fontWeight = fontConfig.weight || 'bold';
        // Use a more realistic default font size (Chart.js default is usually around 12-14px)
        const fontSize = fontConfig.size || 12;

        // Measure text width using canvas context with the actual font
        const ctx = chart.ctx;
        const fontString = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.save();
        ctx.font = fontString;
        const textWidth = ctx.measureText(String(text)).width;
        ctx.restore();

        // For very short bars (less than 80px), always put labels outside
        // This ensures small numbers and "BUY NOW" on very short bars are visible
        const MIN_BAR_WIDTH_FOR_INSIDE = 80;

        if (barWidth < MIN_BAR_WIDTH_FOR_INSIDE) {
            return {
                anchor: 'end',
                align: 'right',
                color: this.colors.ns_base,
                offset: 10
            };
        }

        // Use padding threshold - only keep labels inside if there's clearly enough room
        // Text width + padding must fit comfortably inside the bar
        const padding = 25;
        const fitsInside = textWidth + padding <= barWidth;

        if (fitsInside) {
            // Text fits inside - center it with white color
            return {
                anchor: 'center',
                align: 'center',
                color: 'white'
            };
        } else {
            // Text doesn't fit - position outside (right side) with dark color
            return {
                anchor: 'end',
                align: 'right',
                color: this.colors.ns_base,
                offset: 10 // Offset to position it clearly outside the bar
            };
        }
    }

    updateBarChart() {
        this.sortData();
        const dataEntries = Object.entries(this.data);
        const labels = dataEntries.map(([key]) => this.keyMap[key]);
        const values = dataEntries.map(([key, value]) =>
            this.completedItems.has(key) ? value : value
        );

        const ctx = document.getElementById('barGraph').getContext('2d');

        if (this.chart) {
            this.chart.data.labels = labels;
            this.chart.data.datasets[0].data = values;
            this.chart.data.datasets[0].backgroundColor = this.getBackgroundColors(dataEntries);
            this.chart.update();
            return;
        }

        this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    label: 'Count',
                    data: values,
                    backgroundColor: this.getBackgroundColors(dataEntries),
                    borderColor: this.colors.ns_base,
                    borderWidth: 1,
                    borderRadius: 10,
                }]
            },
            options: {
                ...NativeScriptureChart.CHART_OPTIONS,
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const label = labels[index];
                        const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label);
                        const url = this.urls[normalizedName];
                        if (url) window.open(url, '_blank');
                    }
                },
                plugins: {
                    ...NativeScriptureChart.CHART_OPTIONS.plugins,
                    datalabels: {
                        // Only take color and font from defaults, not anchor/align
                        color: NativeScriptureChart.CHART_OPTIONS.plugins.datalabels.color,
                        font: NativeScriptureChart.CHART_OPTIONS.plugins.datalabels.font,
                        formatter: function (value, context) {
                            const labels = context.chart?.data?.labels || [];
                            const label = labels[context.dataIndex] || '';
                            const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';
                            return this.completedItems.has(normalizedName) ? 'BUY NOW' : value;
                        }.bind(this),
                        anchor: function (context) {
                            const labels = context.chart?.data?.labels || [];
                            const label = labels[context.dataIndex] || '';
                            const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';
                            const text = this.completedItems.has(normalizedName) ? 'BUY NOW' : context.dataset.data[context.dataIndex];
                            const position = this.getLabelPosition(context, String(text));
                            return position.anchor;
                        }.bind(this),
                        align: function (context) {
                            const labels = context.chart?.data?.labels || [];
                            const label = labels[context.dataIndex] || '';
                            const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';
                            const text = this.completedItems.has(normalizedName) ? 'BUY NOW' : context.dataset.data[context.dataIndex];
                            const position = this.getLabelPosition(context, String(text));
                            return position.align;
                        }.bind(this),
                        color: function (context) {
                            const labels = context.chart?.data?.labels || [];
                            const label = labels[context.dataIndex] || '';
                            const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';
                            const text = this.completedItems.has(normalizedName) ? 'BUY NOW' : context.dataset.data[context.dataIndex];
                            const position = this.getLabelPosition(context, String(text));
                            return position.color;
                        }.bind(this),
                        offset: function (context) {
                            const labels = context.chart?.data?.labels || [];
                            const label = labels[context.dataIndex] || '';
                            const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';
                            const text = this.completedItems.has(normalizedName) ? 'BUY NOW' : context.dataset.data[context.dataIndex];
                            const position = this.getLabelPosition(context, String(text));
                            return position.offset || 0;
                        }.bind(this),
                        listeners: {
                            click: function (context) {
                                // Only handle clicks on "BUY NOW" labels
                                const labels = context.chart?.data?.labels || [];
                                const label = labels[context.dataIndex] || '';
                                const normalizedName = Object.keys(this.keyMap).find(key => this.keyMap[key] === label) || '';

                                // Check if this is a completed item with "BUY NOW" text
                                if (this.completedItems.has(normalizedName)) {
                                    const url = this.urls[normalizedName];
                                    if (url) {
                                        window.open(url, '_blank');
                                    }
                                }
                            }.bind(this)
                        }
                    }
                }
            },
            plugins: [ChartDataLabels]
        });
    }
}

// Initialize chart with Shopify metadata, will fetch vote counts from AWS
// Get shopifyMetadata from window (set by leaderboard-section.liquid)
const shopifyMetadata = window.shopifyMetadata || [];
const nsChart = new NativeScriptureChart(shopifyMetadata);

// Refresh vote counts 3 seconds after page load to get updated values
setTimeout(() => {
    nsChart.loadData();
}, 3000);